import React, { useContext, useState } from "react";
import { Form, Input, Button, message } from "lib/ui";
import { AuthContext } from "../../context/Context";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  ArrowRight,
  Sparkles,
  Package,
  Truck,
  BarChart3,
} from "lucide-react";
import Logo from "../../Img/Tidylogo.png";
import "./Login.css";
import ApiPath from "../../hooks/ApiFetch";
import Cookies from "js-cookie";

const FEATURES = [
  { icon: Package, text: "Orders & catalog in one place" },
  { icon: Truck, text: "Drivers, vendors & delivery zones" },
  { icon: BarChart3, text: "Live ops dashboard & reporting" },
];

function BrandMark() {
  const [imgFailed, setImgFailed] = useState(false);

  if (imgFailed) {
    return (
      <div className="login-brand-fallback">
        <span className="login-brand-fallback-icon">T</span>
        <span className="login-brand-fallback-name">Tidyhub</span>
      </div>
    );
  }

  return (
    <img
      src={Logo}
      alt="Tidyhub"
      className="login-brand-logo"
      onError={() => setImgFailed(true)}
    />
  );
}

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { isLoggedIn, setIsLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await ApiPath.post("users/login", {
        password: values.password,
        email: values.email.trim().toLowerCase(),
      });

      const { token, username: user } = response.data;
      Cookies.set("authToken", token, { expires: 1 });
      Cookies.set("userInfo", JSON.stringify(user), { expires: 7 });

      message.success("Welcome back!");
      setIsLoggedIn(true);
      navigate("/");
    } catch (error) {
      const apiMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Invalid email or password. Please try again.";
      message.error(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  if (isLoggedIn) {
    return <Navigate to="/" />;
  }

  return (
    <div className="login-page">
      <div className="login-orb login-orb-a" aria-hidden />
      <div className="login-orb login-orb-b" aria-hidden />
      <div className="login-orb login-orb-c" aria-hidden />

      <div className="login-shell">
        <aside className="login-panel-brand">
          <div className="login-panel-brand-inner">
            <BrandMark />
            <p className="login-eyebrow">
              <Sparkles size={14} strokeWidth={2.5} aria-hidden />
              Admin console
            </p>
            <h1 className="login-headline">
              Run your laundry operations with clarity
            </h1>
            <p className="login-subcopy">
              Secure access for your team — manage orders, customers, drivers,
              and settings from a single professional workspace.
            </p>
            <ul className="login-features">
              {FEATURES.map(({ icon: Icon, text }) => (
                <li key={text} className="login-feature">
                  <span className="login-feature-icon">
                    <Icon size={18} strokeWidth={2} aria-hidden />
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="login-panel-footer">
            © {new Date().getFullYear()} Tidyhub · Internal use only
          </p>
        </aside>

        <main className="login-panel-form">
          <div className="login-card">
            <div className="login-card-header">
              <h2 className="login-card-title">Sign in</h2>
              <p className="login-card-desc">
                Enter your work email and password to continue.
              </p>
            </div>

            <Form
              name="login"
              layout="vertical"
              className="login-form"
              initialValues={{ remember: true }}
              onFinish={handleLogin}
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Enter a valid email address" },
                ]}
              >
                <Input
                  prefix={<Mail size={18} strokeWidth={2} aria-hidden />}
                  type="email"
                  autoComplete="email"
                  placeholder="you@tidyhub.ae"
                  className="login-input"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Password is required" },
                  { min: 6, message: "At least 6 characters" },
                ]}
              >
                <Input
                  prefix={<Lock size={18} strokeWidth={2} aria-hidden />}
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="login-input"
                />
              </Form.Item>

              <div className="login-form-row">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="login-remember-check"
                  />
                  <span>Keep me signed in</span>
                </label>
                <button
                  type="button"
                  className="login-forgot"
                  onClick={() =>
                    message.info("Contact your administrator to reset access.")
                  }
                >
                  Forgot password?
                </button>
              </div>

              <Form.Item className="login-submit-item">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="login-submit"
                  loading={loading}
                  icon={
                    !loading ? (
                      <ArrowRight size={18} strokeWidth={2.5} aria-hidden />
                    ) : null
                  }
                >
                  Sign in to dashboard
                </Button>
              </Form.Item>
            </Form>

            <p className="login-card-foot">
              Protected area. Unauthorized access is prohibited.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
